import React, { useCallback, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  CircularProgress,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { TableVirtuoso } from "react-virtuoso";
import { SortableTableHeaderCell } from "./SortableTableHeaderCell";
import { EditableTableCell } from "./EditableTableCell";
import { custom } from "zod";

export interface DataTableColumn<RowType> {
  columnName: string;
  fieldName?: keyof RowType;
  renderFn?: (row: RowType) => React.ReactNode;
  valueFn?: (row: RowType) => string; // Used for getting editable value and fallback rendering
  clipText?: boolean;
  flex?: number;
  width?: number;
  visibleByDefault?: boolean;
  editable?: boolean | ((row: RowType) => boolean);
}

interface DataTableComponentProps<RowType> {
  rows: RowType[];
  columns: DataTableColumn<RowType>[];
  keyField: keyof RowType;
  onRowClick?: (row: RowType) => void;
  onSort?: (column: DataTableColumn<RowType>) => void;
  sortColumn?: keyof RowType;
  sortDirection?: "asc" | "desc";
  sx?: object;
  style?: React.CSSProperties;
  selectedRowKey?: React.Key;
  stickyHeader?: boolean;
  showColumnVisibilityToggle?: boolean;
  afterLastRow?: React.ReactNode;
  onEndReached?: () => void;
  isLoading?: boolean;
  rowHeight?: number;
  editable?: boolean;
  onCellSave?: (
    row: RowType,
    columnName: string,
    oldValue: any,
    newValue: any
  ) => void;
  onCellChange?: (
    row: RowType,
    columnName: string,
    oldValue: any,
    newValue: any,
    cursorPosition: number
  ) => void;
  onCellEditStart?: (row: RowType, columnName: string) => void;
  onCellEditEnd?: (row: RowType, columnName: string) => void;
  useActiveEditHook?: () => {
    tempId: string;
    columnField: string;
    cursorPosition: number;
    selectAll: boolean;
  } | null;
  rowActions?: (row: RowType) => React.ReactNode;
  getRowSx?: (row: RowType) => object;
  fixedWidth?: number;
  density?: "comfortable" | "compact";
}
export const DataTableComponent = <RowType extends Record<string, any>>({
  rows,
  columns,
  keyField,
  onRowClick,
  onSort,
  sortColumn,
  sortDirection,
  sx,
  style,
  selectedRowKey,
  stickyHeader,
  showColumnVisibilityToggle = false,
  afterLastRow,
  onEndReached,
  isLoading,
  rowHeight = 40,
  editable = false,
  onCellSave,
  onCellChange,
  onCellEditStart,
  onCellEditEnd,
  useActiveEditHook,
  rowActions,
  getRowSx,
  fixedWidth,
  density = "comfortable",
}: DataTableComponentProps<RowType>) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [columnVisibility, setColumnVisibility] = React.useState<
    Record<string, boolean>
  >({});

  React.useEffect(() => {
    setColumnVisibility((prev) => {
      const next = { ...prev };
      columns.forEach((col) => {
        if (!(col.columnName in next)) {
          next[col.columnName] = col.visibleByDefault ?? true;
        }
      });
      Object.keys(next).forEach((key) => {
        if (!columns.some((col) => col.columnName === key)) {
          delete next[key];
        }
      });
      return next;
    });
  }, [columns]);

  const filteredColumns = columns.filter(
    (col) => columnVisibility[col.columnName]
  );

  const totalFlex = filteredColumns.reduce(
    (sum, col) => sum + (col.flex || 1),
    0
  );
  const columnWidths = filteredColumns.map((col) => {
    if (col.width) {
      return col.width;
    } else {
      return `${((col.flex || 1) / totalFlex) * 100}%`;
    }
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleColumnVisibility = (columnName: string) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnName]: !prev[columnName],
    }));
  };

  // Helper to get cell value in priority order: renderFn -> valueFn -> fieldName
  const getCellValue = (
    row: RowType,
    col: DataTableColumn<RowType>
  ): React.ReactNode => {
    if (col.renderFn) {
      return col.renderFn(row);
    }
    if (col.valueFn) {
      return col.valueFn(row);
    }
    if (col.fieldName) {
      return row[col.fieldName as keyof RowType];
    }
    return "";
  };

  const TableH = React.forwardRef<HTMLTableSectionElement>((_props, ref) => (
    <TableHead ref={ref}>
      <TableRow>
        {filteredColumns.map((col, index) => {
          const isSorted = sortColumn && col.fieldName === sortColumn;
          const sortArrow = isSorted
            ? sortDirection === "asc"
              ? " ▲"
              : " ▼"
            : "";

          return (
            <SortableTableHeaderCell
              key={index}
              backgroundColor="#FFF"
              columnName={col.columnName}
              sortArrow={sortArrow}
              width={columnWidths[index]}
              onClick={() => onSort && onSort(col)}
            />
          );
        })}
      </TableRow>
    </TableHead>
  ));

  return (
    <Box
      className="DataTableComponent"
      sx={{
        position: "relative",
        ...sx,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
      style={style}
    >
      {showColumnVisibilityToggle && (
        <IconButton
          size="small"
          onClick={handleMenuOpen}
          sx={{
            position: "absolute",
            top: 4,
            right: 12,
            zIndex: 100,
            backgroundColor: "white",
            border: "1px solid #b9b9b9ff",
            "&:hover": { backgroundColor: "#f5f5f5" },
          }}
        >
          <SettingsIcon />
        </IconButton>
      )}

      <TableContainer
        component={Paper}
        className="TableContainer"
        sx={{
          flex: 1,
          overflow: fixedWidth ? "auto" : "hidden",
          position: "relative",
        }}
      >
        <TableVirtuoso
          data={rows}
          endReached={onEndReached}
          components={{
            Table: ({ style, ...props }) => (
              <Table
                {...props}
                style={{
                  ...style,
                  ...(fixedWidth && { minWidth: `${fixedWidth}px` }),
                }}
                sx={{ tableLayout: "fixed" }}
                stickyHeader={stickyHeader}
              />
            ),
            TableRow: ({ ...props }) => {
              const isSelected = selectedRowKey === props.item[keyField];
              const hasRowActions = rowActions?.(props.item);
              const customRowSx = getRowSx?.(props.item) || {};
              const isEvenRow = props["data-item-index"] % 2 === 0;

              return (
                <TableRow
                  {...props}
                  onClick={() => {
                    // Don't allow row click if there are row actions (temporary rows)
                    if (hasRowActions) return;

                    const selection = window.getSelection();
                    if (selection && selection.type !== "Range") {
                      onRowClick?.(props.item);
                    }
                  }}
                  sx={{
                    cursor: hasRowActions
                      ? "default"
                      : onRowClick
                        ? "pointer"
                        : "default",
                    "&:hover": {
                      backgroundColor: isSelected ? "#c5e4ff" : "#f5f5f5",
                      transition: "background-color 0.15s ease",
                    },
                    backgroundColor: isSelected
                      ? "#d0ebff"
                      : isEvenRow
                        ? "#ffffff"
                        : "#f9f9f9",
                    transition: "background-color 0.15s ease",
                    ...customRowSx,
                  }}
                />
              );
            },
            TableHead: TableH,
            TableBody: React.forwardRef(({ style, ...props }, ref) => (
              <>
                <TableBody
                  className="TableBody"
                  {...props}
                  style={style}
                  ref={ref}
                />
                {afterLastRow}
                {isLoading && (
                  <TableRow>
                    <TableCell
                      colSpan={filteredColumns.length}
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}
              </>
            )),
          }}
          fixedHeaderContent={() => (
            <TableRow>
              {filteredColumns.map((col, index) => {
                const isSorted = col.fieldName === sortColumn;
                const sortArrow = isSorted
                  ? sortDirection === "asc"
                    ? " ▲"
                    : " ▼"
                  : "";
                const isFirstColumn = index === 0;
                return (
                  <SortableTableHeaderCell
                    key={index}
                    columnName={col.columnName}
                    sortArrow={sortArrow}
                    width={columnWidths[index]}
                    onClick={() => onSort && onSort(col)}
                    backgroundColor="#FFF"
                    style={
                      fixedWidth && isFirstColumn
                        ? {
                            position: "sticky",
                            left: 0,
                            zIndex: 3,
                            backgroundColor: "#FFF",
                          }
                        : undefined
                    }
                  />
                );
              })}
            </TableRow>
          )}
          itemContent={(index) => {
            const row = rows[index];
            const actions = rowActions?.(row);
            const actualRowHeight = density === "comfortable" ? 48 : 40;
            const cellPadding = density === "comfortable" ? "12px" : "8px";

            return (
              <>
                {actions && (
                  <TableCell
                    key="row-actions"
                    style={{
                      width: filteredColumns[0]?.width || columnWidths[0],
                      verticalAlign: "middle",
                      height: `${actualRowHeight}px`,
                      padding: cellPadding,
                      boxSizing: "border-box",
                      position: fixedWidth ? "sticky" : "relative",
                      ...(fixedWidth && {
                        left: 0,
                        zIndex: 2,
                        backgroundColor: "inherit",
                      }),
                      borderRight: "1px solid #e0e0e0",
                    }}
                  >
                    {actions}
                  </TableCell>
                )}
                {filteredColumns.map((col, colIndex) => {
                  // Skip first column if we have actions (actions replace first column)
                  if (actions && colIndex === 0) return null;

                  // Determine if this cell should be editable
                  const isColumnEditable =
                    col.editable !== undefined
                      ? typeof col.editable === "function"
                        ? col.editable(row)
                        : col.editable
                      : false;

                  // Get the cell display value
                  const cellDisplayValue = getCellValue(row, col);

                  // Get the editable value (valueFn takes priority, then fallback to display value)
                  const editableValue = col.valueFn
                    ? col.valueFn(row)
                    : cellDisplayValue;

                  // Only make cell editable if editable value is primitive (string or number)
                  const isPrimitiveValue =
                    typeof editableValue === "string" ||
                    typeof editableValue === "number";
                  const isCellEditable =
                    editable && isColumnEditable && isPrimitiveValue;

                  const actualRowHeight = density === "comfortable" ? 48 : 40;
                  const cellPadding =
                    density === "comfortable" ? "12px" : "8px";

                  return (
                    <TableCell
                      key={colIndex}
                      style={{
                        whiteSpace: col.clipText ? "nowrap" : "normal",
                        wordBreak: col.clipText ? "normal" : "break-word",
                        fontSize: "14px",
                        fontWeight: 500,
                        overflow: col.clipText ? "hidden" : "visible",
                        textOverflow: col.clipText ? "ellipsis" : "",
                        width: columnWidths[colIndex],
                        verticalAlign: "middle",
                        height: `${actualRowHeight}px`,
                        padding: cellPadding,
                        boxSizing: "border-box",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      {isCellEditable ? (
                        <EditableTableCell
                          value={editableValue as string | number}
                          displayValue={cellDisplayValue}
                          onSave={(newValue) => {
                            const oldValue = editableValue;
                            if (onCellSave) {
                              // Use fieldName if available, otherwise fall back to columnName
                              const propertyName = String(col.fieldName!);
                              onCellSave(row, propertyName, oldValue, newValue);
                            }
                          }}
                          onChange={
                            onCellChange
                              ? (newValue, cursorPosition) => {
                                  const oldValue = editableValue;
                                  const propertyName = String(col.fieldName!);
                                  onCellChange(
                                    row,
                                    propertyName,
                                    oldValue,
                                    newValue,
                                    cursorPosition
                                  );
                                }
                              : undefined
                          }
                          onEditStart={() => {
                            const propertyName = String(col.fieldName!);
                            onCellEditStart?.(row, propertyName);
                          }}
                          onEditEnd={() => {
                            const propertyName = String(col.fieldName!);
                            onCellEditEnd?.(row, propertyName);
                          }}
                          useActiveEditHook={useActiveEditHook}
                          rowId={String(row[keyField])}
                          columnField={String(col.fieldName)}
                          clipText={col.clipText}
                          width={columnWidths[colIndex]}
                          rowHeight={rowHeight}
                        />
                      ) : (
                        cellDisplayValue
                      )}
                    </TableCell>
                  );
                })}
              </>
            );
          }}
          totalCount={rows.length}
          style={{ height: "100%" }}
          fixedItemHeight={rowHeight}
        />

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          MenuListProps={{
            "aria-labelledby": "column-visibility-menu",
          }}
        >
          {columns.map((col) => (
            <MenuItem key={col.columnName} dense>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={columnVisibility[col.columnName]}
                    onChange={() => toggleColumnVisibility(col.columnName)}
                  />
                }
                label={col.columnName}
                sx={{ ml: 0 }}
              />
            </MenuItem>
          ))}
        </Menu>
      </TableContainer>
    </Box>
  );
};
